import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Clock, Heart, BookOpen, ChevronRight, Plus, Headphones } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import Navigation from '@/components/Navigation';
import AudioCard from '@/components/AudioCard';
import WaveLoader from '@/components/WaveLoader';
import { getProgress, getFavorites, getJournalEntries, createJournalEntry, getMoodEntries, getListeningStats, getInnerShiftProgress } from '@/lib/api';
import { useFeatures } from '@/contexts/FeatureContext';
import { toast } from 'sonner';
import { Trophy } from 'lucide-react';

export default function ProfilePage() {
  const [progress, setProgress] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [moodHistory, setMoodHistory] = useState([]);
  const [listeningStats, setListeningStats] = useState(null);
  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newJournal, setNewJournal] = useState({ title: '', content: '', mood: null });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const features = useFeatures();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const promises = [
        getProgress(),
        getFavorites().catch(() => ({ data: [] })),
        getJournalEntries(),
        getMoodEntries(7),
      ];
      if (features.listening_stats) {
        promises.push(getListeningStats().catch(() => ({ data: null })));
      }
      promises.push(getInnerShiftProgress().catch(() => ({ data: null })));
      const results = await Promise.all(promises);
      setProgress(results[0].data);
      setFavorites(results[1].data || []);
      setJournalEntries(results[2].data);
      setMoodHistory(results[3].data);
      // results[4] is listening stats if enabled, else it's the shift result
      if (features.listening_stats) {
        if (results[4]) setListeningStats(results[4].data);
        if (results[5]) setShift(results[5].data);
      } else if (results[4]) {
        setShift(results[4].data);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateJournal = async () => {
    if (!newJournal.title.trim() || !newJournal.content.trim()) {
      toast.error('Please fill in title and content');
      return;
    }

    try {
      const response = await createJournalEntry(newJournal);
      setJournalEntries([response.data, ...journalEntries]);
      setNewJournal({ title: '', content: '', mood: null });
      setIsDialogOpen(false);
      toast.success('Journal entry saved');
    } catch (error) {
      toast.error('Failed to save journal entry');
    }
  };

  const handleFavoriteChange = (trackId, isFavorite) => {
    if (!isFavorite) {
      setFavorites(favorites.filter(f => f.id !== trackId));
    }
  };

  const moodEmojis = ['😔', '😐', '🙂', '😊', '🤩'];

  if (loading) {
    return <WaveLoader />;
  }

  return (
    <div className="min-h-screen bg-zinc-950 pb-24 md:pb-8 md:pt-24">
      <Navigation />

      <main className="container mx-auto px-4 md:px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-serif text-white mb-2">Your Journey</h1>
          <p className="text-zinc-500">Track your progress and growth</p>
        </div>

        {/* Inner Shift Badge */}
        {shift?.badge_earned && (
          <div
            className="mb-8 p-5 rounded-2xl bg-gradient-to-r from-amber-500/15 via-zinc-900/60 to-violet-500/15 border border-amber-500/30 flex items-center gap-4"
            data-testid="inner-shift-badge"
          >
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
              <Trophy className="w-6 h-6 text-amber-400" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] tracking-[0.2em] uppercase text-amber-300">Achievement</p>
              <p className="text-base font-serif text-white">21-Day Inner Shift Complete</p>
              <p className="text-xs text-zinc-500">
                Longest streak: {shift.longest_streak || 21} days · Reward track unlocked
              </p>
            </div>
          </div>
        )}
        {shift && !shift.badge_earned && shift.today_track && (
          <div
            className="mb-8 p-4 rounded-2xl bg-zinc-900/50 border border-amber-500/20 flex items-center gap-3"
            data-testid="inner-shift-profile-progress"
          >
            <Flame className="w-5 h-5 text-amber-400 shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-white">
                Day {Math.min(shift.current_day, 21)} of 21 · {shift.current_streak} day streak
              </p>
              <p className="text-xs text-zinc-500">
                {Math.max(0, 21 - (shift.completed_count || 0))} days to your Inner Shift
              </p>
            </div>
            <Link
              to="/dashboard"
              className="text-xs text-amber-400 hover:text-amber-300 underline underline-offset-2"
            >
              Continue
            </Link>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="profile-stats">
          {/* Streak */}
          <div className="bg-gradient-to-br from-amber-900/20 via-zinc-900 to-zinc-950 rounded-2xl border border-amber-500/20 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Flame className="w-5 h-5 text-amber-400" />
              <span className="text-sm text-amber-400">Streak</span>
            </div>
            <p className="text-3xl font-serif text-white">{progress?.current_streak || 0}</p>
            <p className="text-xs text-zinc-600 mt-1">days</p>
          </div>

          {/* Total Sessions */}
          <div className="bg-zinc-900/50 rounded-2xl border border-white/5 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-teal-400" />
              <span className="text-sm text-teal-400">Sessions</span>
            </div>
            <p className="text-3xl font-serif text-white">{progress?.total_sessions || 0}</p>
            <p className="text-xs text-zinc-600 mt-1">completed</p>
          </div>

          {/* Minutes */}
          <div className="bg-zinc-900/50 rounded-2xl border border-white/5 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-violet-400" />
              <span className="text-sm text-violet-400">Time</span>
            </div>
            <p className="text-3xl font-serif text-white">{Math.round((progress?.total_minutes || 0) / 60)}</p>
            <p className="text-xs text-zinc-600 mt-1">hours</p>
          </div>

          {/* Favorites */}
          <div className="bg-zinc-900/50 rounded-2xl border border-white/5 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Heart className="w-5 h-5 text-red-400" />
              <span className="text-sm text-red-400">Favorites</span>
            </div>
            <p className="text-3xl font-serif text-white">{favorites.length}</p>
            <p className="text-xs text-zinc-600 mt-1">saved</p>
          </div>
        </div>

        {/* Listening Stats */}
        {features.listening_stats && listeningStats && listeningStats.total_minutes > 0 && (
          <div className="bg-zinc-900/50 rounded-2xl border border-violet-500/10 p-5 mb-10" data-testid="listening-stats">
            <div className="flex items-center gap-2 mb-3">
              <Headphones className="w-5 h-5 text-violet-400" />
              <h2 className="text-base font-medium text-violet-400">Listening Stats</h2>
            </div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-2xl font-serif text-white">{listeningStats.total_minutes}</span>
              <span className="text-xs text-zinc-500">minutes listened</span>
            </div>
            {listeningStats.last_played_at && (
              <p className="text-[11px] text-zinc-600">Last listened: {new Date(listeningStats.last_played_at).toLocaleDateString()}</p>
            )}
          </div>
        )}

        {/* Mood History */}
        {moodHistory.length > 0 && (
          <section className="mb-10" data-testid="mood-history">
            <h2 className="text-xl font-serif text-white mb-4">Recent Moods</h2>
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {moodHistory.slice(0, 7).map((entry, index) => (
                <div 
                  key={entry.id || index}
                  className="flex flex-col items-center gap-1 bg-zinc-900/50 rounded-xl p-3 min-w-[60px]"
                >
                  <span className="text-2xl">{moodEmojis[entry.mood - 1]}</span>
                  <span className="text-xs text-zinc-600">
                    {new Date(entry.date).toLocaleDateString('en', { weekday: 'short' })}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Favorites Section */}
        {favorites.length > 0 && (
          <section className="mb-10" data-testid="favorites-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-serif text-white">Saved Tracks</h2>
              <Link to="/library" className="text-sm text-zinc-500 hover:text-zinc-300 flex items-center gap-1">
                Browse more <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {favorites.slice(0, 4).map((track) => (
                <AudioCard 
                  key={track.id} 
                  track={track}
                  isFavorite={true}
                  onFavoriteChange={handleFavoriteChange}
                />
              ))}
            </div>
          </section>
        )}

        {/* Journal Section */}
        <section data-testid="journal-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-violet-400" />
              <h2 className="text-xl font-serif text-white">Journal</h2>
            </div>
            
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="bg-white/5 border-white/10 hover:bg-white/10 text-zinc-300 rounded-full"
                  data-testid="new-journal-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Entry
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-white/10 text-white">
                <DialogHeader>
                  <DialogTitle className="font-serif text-xl">New Journal Entry</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <Input
                    placeholder="Title"
                    value={newJournal.title}
                    onChange={(e) => setNewJournal({ ...newJournal, title: e.target.value })}
                    className="bg-zinc-800 border-white/10"
                    data-testid="journal-title-input"
                  />
                  <Textarea
                    placeholder="What's on your mind?"
                    value={newJournal.content}
                    onChange={(e) => setNewJournal({ ...newJournal, content: e.target.value })}
                    className="bg-zinc-800 border-white/10 min-h-[120px]"
                    data-testid="journal-content-input"
                  />
                  <div>
                    <p className="text-sm text-zinc-500 mb-2">How are you feeling?</p>
                    <div className="flex gap-2">
                      {moodEmojis.map((emoji, index) => (
                        <button
                          key={index}
                          onClick={() => setNewJournal({ ...newJournal, mood: index + 1 })}
                          className={`text-2xl p-2 rounded-lg transition-colors ${
                            newJournal.mood === index + 1 
                              ? 'bg-violet-600' 
                              : 'bg-zinc-800 hover:bg-zinc-700'
                          }`}
                          data-testid={`journal-mood-${index + 1}`}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Button 
                    onClick={handleCreateJournal}
                    className="w-full bg-white text-zinc-900 hover:bg-zinc-200"
                    data-testid="save-journal-btn"
                  >
                    Save Entry
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Journal Entries */}
          {journalEntries.length === 0 ? (
            <div className="bg-zinc-900/50 rounded-2xl border border-white/5 p-8 text-center">
              <BookOpen className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">No journal entries yet.</p>
              <p className="text-sm text-zinc-600 mt-1">Start reflecting on your journey.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {journalEntries.slice(0, 5).map((entry) => (
                <div 
                  key={entry.id}
                  className="bg-zinc-900/50 rounded-2xl border border-white/5 p-5 hover:border-white/10 transition-colors"
                  data-testid={`journal-entry-${entry.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {entry.mood && (
                          <span className="text-lg">{moodEmojis[entry.mood - 1]}</span>
                        )}
                        <h3 className="font-medium text-white">{entry.title}</h3>
                      </div>
                      <p className="text-sm text-zinc-500 line-clamp-2">{entry.content}</p>
                    </div>
                    <span className="text-xs text-zinc-600 whitespace-nowrap">
                      {new Date(entry.created_at).toLocaleDateString('en', { 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
